/**
 * Guided "Fix Days" auto-advance signal — shared by PlanningHub (the direct
 * "Legacy: Direct confirm regeneration" path and the PlanFoodLauncher
 * onPlanGenerated callback) and PlanReview.handleSave.
 *
 * Prior behavior stored a bare '1' flag meaning "some plan generation just
 * happened while Fix Mode was active" with no record of WHICH date it was
 * for. PlanningHub's auto-advance effect trusted that flag unconditionally,
 * so a completely unrelated action — closing the Fix Days launcher, then
 * using the ordinary "Plan Food" button to generate a different date —
 * could silently advance the Fix Days queue past the real, still-broken
 * target day while showing a false "problem day resolved" toast (see
 * AGENTS.md pre-launch audit, finding FIXDAYS-1).
 *
 * The signal now carries the date(s) actually handled, and the consumer
 * only advances when the currently queued target date is among them.
 */

export const FIX_MODE_KEY = 'fitxess.fixMode';
export const FIX_MODE_ADVANCE_KEY = 'fitxess.fixModeAdvancePending';

/** Record that a plan generation/regeneration/save just handled these date(s). */
export function setFixModeAdvancePending(handledDates) {
  var dates = Array.isArray(handledDates) ? handledDates : [handledDates];
  try {
    sessionStorage.setItem(FIX_MODE_ADVANCE_KEY, JSON.stringify(dates));
  } catch (e) { /* ignore */ }
}

/**
 * Reads and clears the one-shot advance signal (always consumed, whether or
 * not it matches — it's stale either way once checked). Returns true only
 * when `targetDate` (the currently queued Fix Days target) is among the
 * date(s) that were actually handled.
 */
export function consumeFixModeAdvanceSignal(targetDate) {
  var raw;
  try {
    raw = sessionStorage.getItem(FIX_MODE_ADVANCE_KEY);
    sessionStorage.removeItem(FIX_MODE_ADVANCE_KEY);
  } catch (e) {
    return false;
  }
  if (!raw) return false;
  try {
    var dates = JSON.parse(raw);
    // A bare legacy '1' (pre-fix session in another stale tab) parses to the
    // number 1, not an array — never trust it as a match.
    if (!Array.isArray(dates)) return false;
    return dates.indexOf(targetDate) !== -1;
  } catch (e) {
    return false;
  }
}

/**
 * Clears all Fix Days session state. Call on logout/account switch so a
 * stale Fix Mode banner/queue can never leak into a different user's
 * session on a shared device/tab (see AGENTS.md pre-launch audit, finding
 * FIXDAYS-3).
 */
export function clearFixModeState() {
  try {
    sessionStorage.removeItem(FIX_MODE_KEY);
    sessionStorage.removeItem(FIX_MODE_ADVANCE_KEY);
  } catch (e) { /* ignore */ }
}
